import { skipToken } from "@reduxjs/toolkit/query";
import {
  IntegrationProvider,
  type GenesysIntegration,
  type GenesysIntegrationRequest,
  type IntegrationConnectionTestResponse,
} from "@repo/app-types/entities/integration";
import { buildEntityTag, TagType } from "../helpers/tags.js";
import { api } from "../redux.js";

/**
 * The Genesys integration comes from its own route rather than the boot
 * payload: the stored settings carry the encrypted client secret, which no
 * customer response may include, so the API returns a non-secret view instead.
 *
 * The secret travels one way only — every credential write posts it again, and
 * nothing ever reads it back, so the detail view renders a fixed mask.
 */

export interface GenesysIntegrationResponse {
  integration: GenesysIntegration | null;
}

const GENESYS_TAG_ID = "genesys";

export const genesysIntegrationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    genesysIntegration: builder.query<GenesysIntegrationResponse, void>({
      query: () => "/integrations/genesys",
      providesTags: () => [
        buildEntityTag(TagType.GenesysIntegration, GENESYS_TAG_ID),
      ],
    }),

    /**
     * Probes typed credentials without storing them. Always carries the full
     * credentials because the stored secret is write-only — there is no
     * server-side test to run against a connection the customer isn't editing.
     */
    testGenesysConnection: builder.mutation<
      IntegrationConnectionTestResponse,
      GenesysIntegrationRequest
    >({
      query: (credentials) => ({
        method: "POST",
        url: "/integrations/test-connection",
        body: { provider: IntegrationProvider.GENESYS, ...credentials },
      }),
    }),

    connectGenesysIntegration: builder.mutation<
      GenesysIntegration,
      GenesysIntegrationRequest,
      { integration: GenesysIntegration }
    >({
      query: (credentials) => ({
        method: "POST",
        url: "/integrations",
        body: { provider: IntegrationProvider.GENESYS, ...credentials },
      }),
      transformResponse: (response) => response.integration,
      invalidatesTags: () => [
        buildEntityTag(TagType.GenesysIntegration, GENESYS_TAG_ID),
      ],
    }),

    updateGenesysIntegration: builder.mutation<
      GenesysIntegration,
      { integrationId: string; credentials: GenesysIntegrationRequest },
      { integration: GenesysIntegration }
    >({
      query: ({ integrationId, credentials }) => ({
        method: "PATCH",
        url: `/integrations/${integrationId}`,
        body: credentials,
      }),
      transformResponse: (response) => response.integration,
      invalidatesTags: () => [
        buildEntityTag(TagType.GenesysIntegration, GENESYS_TAG_ID),
      ],
    }),

    setGenesysIntegrationEnabled: builder.mutation<
      GenesysIntegration,
      { integrationId: string; isEnabled: boolean },
      { integration: GenesysIntegration }
    >({
      // Shares the credential route: the union body means pausing never has to
      // carry the client secret.
      query: ({ integrationId, isEnabled }) => ({
        method: "PATCH",
        url: `/integrations/${integrationId}`,
        body: { is_enabled: isEnabled },
      }),
      transformResponse: (response) => response.integration,
      invalidatesTags: () => [
        buildEntityTag(TagType.GenesysIntegration, GENESYS_TAG_ID),
      ],
    }),

    disconnectGenesysIntegration: builder.mutation<
      void,
      { integrationId: string }
    >({
      query: ({ integrationId }) => ({
        method: "DELETE",
        url: `/integrations/${integrationId}`,
      }),
      invalidatesTags: () => [
        buildEntityTag(TagType.GenesysIntegration, GENESYS_TAG_ID),
      ],
    }),
  }),
});

/** Subscribes to the status only when Genesys is actually on for this company. */
export function useGenesysIntegration(enabled: boolean) {
  return genesysIntegrationApi.useGenesysIntegrationQuery(
    enabled ? undefined : skipToken,
    { refetchOnMountOrArgChange: true },
  );
}

export const {
  useConnectGenesysIntegrationMutation,
  useDisconnectGenesysIntegrationMutation,
  useSetGenesysIntegrationEnabledMutation,
  useTestGenesysConnectionMutation,
  useUpdateGenesysIntegrationMutation,
} = genesysIntegrationApi;
